import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Phone, MapPin, Mail, Building2,
  Calendar, CheckCircle2, XCircle, ShoppingBag,
  Pencil, Plus, Clock, Hash, RefreshCw, RotateCcw, MinusCircle, ExternalLink
} from "lucide-react";
import { getClient, getClientAppointments, revokeOutcome, updateAppointment, updateClient } from "@/utils/api";
import ClientForm from "@/components/ClientForm";
import AppointmentScheduler from "@/components/AppointmentScheduler";
import { toast } from "sonner";

const OUTCOME_CONFIG = {
  visited: { label: "Fatto Visita", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100" },
  not_found: { label: "Non Trovato", icon: XCircle, color: "text-amber-600", bg: "bg-amber-50 border-amber-100" },
  not_passed: { label: "Non Passato", icon: MinusCircle, color: "text-slate-500", bg: "bg-slate-50 border-slate-100" },
  order: { label: "Fatto Ordine", icon: ShoppingBag, color: "text-violet-600", bg: "bg-violet-50 border-violet-100" },
};

const FREQ_LABEL = {
  1: "Ogni settimana",
  2: "Ogni 2 settimane",
  3: "Ogni 3 settimane",
  4: "Ogni 4 settimane (mensile)",
  6: "Ogni 6 settimane",
  8: "Ogni 8 settimane (bimestrale)",
  12: "Ogni 12 settimane (trimestrale)",
};

const DAY_LABEL_FULL = {
  0: "Lunedì", 1: "Martedì", 2: "Mercoledì",
  3: "Giovedì", 4: "Venerdì", 5: "Sabato", 6: "Domenica"
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [editingNextDate, setEditingNextDate] = useState(false);
  const [nextDateVal, setNextDateVal] = useState("");
  const [nextTimeVal, setNextTimeVal] = useState("");
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetVal, setTargetVal] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, aRes] = await Promise.all([
        getClient(id),
        getClientAppointments(id),
      ]);
      setClient(cRes.data);
      setAppointments(aRes.data);
    } catch {
      toast.error("Errore nel caricamento del cliente");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (aptId) => {
    try {
      await revokeOutcome(aptId);
      toast.success("Esito annullato, appuntamento ripristinato");
      load();
    } catch {
      toast.error("Errore nell'annullamento dell'esito");
    }
  };

  if (loading) return <DetailSkeleton />;
  if (!client) return (
    <div className="p-6 text-center text-slate-500">Cliente non trovato</div>
  );

  const completed = appointments.filter((a) => a.status === "completed");
  const pending = appointments.filter((a) => a.status === "pending");
  const lastVisit = completed[0];
  const nextVisit = pending.sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0];

  const totalOrders = completed.filter((a) => a.outcome === "order");
  const visitRate = completed.length > 0
    ? Math.round((completed.filter((a) => a.outcome === "visited" || a.outcome === "order").length / completed.length) * 100)
    : 0;

  return (
    <div className="p-4 md:p-6 space-y-5 fade-in max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/app/clienti")}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#0F172A] truncate">{client.name}</h1>
          {client.company && <p className="text-sm text-slate-500">{client.company}</p>}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            data-testid="schedule-visit-btn"
            onClick={() => setShowScheduler(true)}
            className="flex items-center gap-1.5 bg-[#0F172A] text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors active:scale-95"
          >
            <Plus className="w-4 h-4" />
            Visita
          </button>
          <button
            data-testid="edit-client-btn"
            onClick={() => setShowEdit(true)}
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <Pencil className="w-4 h-4 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-3">
        <MiniStat
          icon={<RefreshCw className="w-4 h-4 text-blue-500" />}
          label="Frequenza"
          value={FREQ_LABEL[client.frequency_weeks] || `${client.frequency_weeks} sett.`}
          compact
        />
        <MiniStat
          icon={<Calendar className="w-4 h-4 text-indigo-500" />}
          label="Giorno visita"
          value={client.preferred_day != null ? DAY_LABEL_FULL[client.preferred_day] : "—"}
          compact
        />
        <MiniStat
          icon={<ShoppingBag className="w-4 h-4 text-violet-500" />}
          label="Ordini"
          value={totalOrders.length}
        />
      </div>

      {/* Next/Last visit */}
      {(nextVisit || lastVisit) && (
        <div className="grid grid-cols-2 gap-3">
          {nextVisit && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">Prossima visita</div>
                <button
                  onClick={() => {
                    setNextDateVal(nextVisit.scheduled_date);
                    setNextTimeVal(nextVisit.scheduled_time || "");
                    setEditingNextDate(true);
                  }}
                  className="text-blue-400 hover:text-blue-600 transition-colors"
                  data-testid="edit-next-date-btn"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              </div>
              {editingNextDate ? (
                <div className="mt-1.5 space-y-1.5">
                  <input
                    type="date"
                    value={nextDateVal}
                    onChange={e => setNextDateVal(e.target.value)}
                    className="w-full text-xs border border-blue-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="next-date-input"
                  />
                  <input
                    type="time"
                    value={nextTimeVal}
                    onChange={e => setNextTimeVal(e.target.value)}
                    className="w-full text-xs border border-blue-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
                    data-testid="next-time-input"
                  />
                  <div className="flex gap-1.5">
                    <button
                      onClick={async () => {
                        if (!nextDateVal) return;
                        try {
                          await updateAppointment(nextVisit.id, {
                            scheduled_date: nextDateVal,
                            scheduled_time: nextTimeVal || null,
                          });
                          toast.success("Data aggiornata");
                          setEditingNextDate(false);
                          load();
                        } catch { toast.error("Errore nell'aggiornamento"); }
                      }}
                      className="flex-1 text-xs bg-blue-500 text-white py-1 rounded-lg font-semibold"
                      data-testid="save-next-date-btn"
                    >Salva</button>
                    <button
                      onClick={() => setEditingNextDate(false)}
                      className="flex-1 text-xs bg-white border border-blue-200 text-blue-500 py-1 rounded-lg"
                    >Annulla</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="font-bold text-[#0F172A] text-sm mt-0.5">
                    {format(parseISO(nextVisit.scheduled_date), "dd/MM/yyyy")}
                    {nextVisit.scheduled_time && <span className="ml-1 font-normal text-blue-400">ore {nextVisit.scheduled_time}</span>}
                  </div>
                  <div className="text-[10px] text-blue-400 capitalize">
                    {format(parseISO(nextVisit.scheduled_date), "EEEE", { locale: it })}
                  </div>
                </>
              )}
            </div>
          )}
          {lastVisit && (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Ultima visita</div>
              <div className="font-bold text-[#0F172A] text-sm mt-0.5">
                {format(parseISO(lastVisit.outcome_date || lastVisit.scheduled_date), "dd/MM/yyyy")}
              </div>
              {lastVisit.outcome && (
                <div className={`text-[10px] font-medium mt-0.5 ${OUTCOME_CONFIG[lastVisit.outcome]?.color}`}>
                  {OUTCOME_CONFIG[lastVisit.outcome]?.label}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Contact info */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3">
        <h2 className="font-heading font-semibold text-[#0F172A]">Informazioni</h2>
        <div className="space-y-2">
          {client.phone && (
            <InfoRow icon={<Phone className="w-4 h-4" />} label="Telefono">
              <a href={`tel:${client.phone}`} className="text-blue-600 hover:underline" data-testid="detail-phone">
                {client.phone}
              </a>
            </InfoRow>
          )}
          {client.email && (
            <InfoRow icon={<Mail className="w-4 h-4" />} label="Email">
              <a href={`mailto:${client.email}`} className="text-blue-600 hover:underline" data-testid="detail-email">
                {client.email}
              </a>
            </InfoRow>
          )}
          {(client.address || client.city) && (
            <InfoRow icon={<MapPin className="w-4 h-4" />} label="Indirizzo">
              <span data-testid="detail-address">
                {[client.address, client.city].filter(Boolean).join(", ")}
              </span>
              {" "}
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([client.company || client.name, client.address, client.city].filter(Boolean).join(" "))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 text-[11px] text-blue-500 hover:text-blue-700 transition-colors ml-1"
                title="Cerca su Google Maps"
              >
                <ExternalLink className="w-3 h-3" />
                Maps
              </a>
            </InfoRow>
          )}

          {client.last_order_date && (
            <InfoRow icon={<ShoppingBag className="w-4 h-4 text-violet-500" />} label="Ultimo Ordine">
              <span data-testid="detail-last-order" className="font-semibold text-violet-700">
                {new Date(client.last_order_date).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
              </span>
              {client.last_order_amount != null && (
                <span className="ml-2 text-sm text-slate-500">
                  · €{client.last_order_amount.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                </span>
              )}
            </InfoRow>
          )}
          {client.zone && (
            <InfoRow icon={<Building2 className="w-4 h-4" />} label="Zona">
              <span data-testid="detail-zone">{client.zone}</span>
            </InfoRow>
          )}

          {/* Target annuale */}
          <InfoRow icon={<span className="text-sm">🎯</span>} label="Target annuale">
            {editingTarget ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const val = parseFloat(targetVal.replace(",", "."));
                  await updateClient(id, { target_annual: isNaN(val) ? null : val }, token);
                  setClient(c => ({ ...c, target_annual: isNaN(val) ? null : val }));
                  setEditingTarget(false);
                }}
                className="flex items-center gap-2"
              >
                <span className="text-slate-400 text-sm">€</span>
                <input
                  autoFocus
                  type="number"
                  min="0"
                  step="100"
                  value={targetVal}
                  onChange={e => setTargetVal(e.target.value)}
                  placeholder="es. 10000"
                  className="border border-slate-300 rounded-lg px-2 py-1 text-sm w-28 focus:outline-none focus:border-slate-500"
                  data-testid="target-annual-input"
                />
                <button type="submit" className="text-xs bg-[#0F172A] text-white px-3 py-1 rounded-lg">Salva</button>
                <button type="button" onClick={() => setEditingTarget(false)} className="text-xs text-slate-400">Annulla</button>
              </form>
            ) : (
              <button
                data-testid="edit-target-btn"
                onClick={() => { setTargetVal(client.target_annual ?? ""); setEditingTarget(true); }}
                className="text-sm text-[#0F172A] hover:underline"
              >
                {client.target_annual ? `€ ${Number(client.target_annual).toLocaleString("it-IT")}` : <span className="text-slate-400 italic">Non impostato — tocca per aggiungere</span>}
              </button>
            )}
          </InfoRow>

          {client.notes && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <span>&#128161;</span> Promemoria prossima visita
              </p>
              <p className="text-sm text-slate-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100" data-testid="detail-notes">{client.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Visit history */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="font-heading font-semibold text-[#0F172A]">
            Storico Visite ({appointments.length})
          </h2>
        </div>
        {appointments.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400">Nessuna visita ancora</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {appointments.map((apt) => {
              const outcome = apt.outcome ? OUTCOME_CONFIG[apt.outcome] : null;
              const Icon = outcome?.icon;
              const isPending = apt.status === "pending";
              const dateStr = format(parseISO(apt.scheduled_date), "dd/MM/yyyy");
              return (
                <div key={apt.id} data-testid={`history-apt-${apt.id}`} className="flex items-start gap-3 px-5 py-3.5">
                  <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${isPending ? "bg-blue-400" : "bg-slate-300"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-[#0F172A]">{dateStr}</span>
                      <div className="flex items-center gap-2">
                        {!isPending && outcome && (
                          <button
                            data-testid={`revoke-history-${apt.id}`}
                            onClick={() => handleRevoke(apt.id)}
                            title="Annulla esito"
                            className="p-1 rounded text-slate-300 hover:text-red-400 transition-colors"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {isPending ? (
                          <Badge variant="secondary" className="text-[10px]">In attesa</Badge>
                        ) : outcome ? (
                          <div className={`flex items-center gap-1 text-xs font-medium ${outcome.color}`}>
                            <Icon className="w-3 h-3" />
                            {outcome.label}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    {apt.outcome_notes && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate">{apt.outcome_notes}</p>
                    )}
                    {apt.order_amount && (
                      <p className="text-xs text-violet-600 font-medium mt-0.5">
                        Ordine: €{apt.order_amount.toFixed(2)}
                      </p>
                    )}
                    {apt.notes && !apt.outcome_notes && (
                      <p className="text-xs text-slate-400 mt-0.5 italic truncate">{apt.notes}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ClientForm
        open={showEdit}
        onClose={() => setShowEdit(false)}
        onSuccess={load}
        client={client}
      />
      <AppointmentScheduler
        open={showScheduler}
        onClose={() => setShowScheduler(false)}
        onSuccess={load}
        preselectedClientId={client.id}
      />
    </div>
  );
}

function InfoRow({ icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="text-slate-400 mt-0.5 flex-shrink-0">{icon}</div>
      <div>
        <div className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-slate-700">{children}</div>
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value, compact }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">{label}</span>
      </div>
      <div className={`font-bold text-[#0F172A] ${compact ? "text-xs leading-tight" : "text-lg"}`}>{value}</div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="p-4 md:p-6 space-y-5 max-w-2xl mx-auto">
      <Skeleton className="h-10 w-48 rounded-xl" />
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
      </div>
      <Skeleton className="h-32 w-full rounded-2xl" />
      <Skeleton className="h-48 w-full rounded-2xl" />
    </div>
  );
}
